import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  computed,
  signal,
} from '@angular/core';
import { toSignal, toObservable, rxResource } from '@angular/core/rxjs-interop';
import {
  distinctUntilChanged,
  filter,
  take,
  tap,
  catchError,
  timeout,
  exhaustMap,
  switchMap,
  map,
} from 'rxjs/operators';
import { Ag1TableService } from '../../ag1-table.service';
import { FieldType } from '@app/core/interfaces/table.interface';
import { ItemType } from '@app/core/components/ag1-dropdown-button/ag1-dropdown-button.component';
import {
  Ag1TableFieldAttribute,
  Ag1TableOutputFileResult,
  Ag1TableOutputFileResultGQL,
  GenerateAg1TableOutputFileGQL,
  GenerateAg1TableOutputFileMutationVariables,
  OnAg1TableOutputFileCompleteGQL,
  TemplateFileFormatEnum,
} from '@app/generated/graphql';
import { ActivatedRoute } from '@angular/router';
import { captureException } from '@sentry/angular';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { SentenceCasePipe } from '@app/core/pipes/sentence.pipe';
import { Ag1DropdownButtonComponent } from '../../../ag1-dropdown-button/ag1-dropdown-button.component';
import { MessageService } from '@openng/optimus-ui/api';
import { UUIDService } from '@app/core/services/uuid/uuid.service';
import { EMPTY, Observable, concat, interval, merge } from 'rxjs';

@Component({
  selector: 'ag1-table-download',
  templateUrl: './ag1-table-download.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Ag1DropdownButtonComponent],
})
export class Ag1TableDownloadComponent {
  private _messageService = inject(MessageService);
  private _uuidService = inject(UUIDService);
  private _route = inject(ActivatedRoute);
  private _generatedAg1TableOutputFileGQL = inject(
    GenerateAg1TableOutputFileGQL,
  );
  private _onAg1TableOutputFileCompleteGQL = inject(
    OnAg1TableOutputFileCompleteGQL,
  );
  private _ag1TableOutputFileResultGQL = inject(Ag1TableOutputFileResultGQL);
  private _humanizePipe = inject(HumanizePipe);
  private _sentenceCasePipe = inject(SentenceCasePipe);

  // Modern Signal Inputs
  readonly tableService = input.required<Ag1TableService>();
  readonly tableName = input.required<string>();
  readonly hasPdfFormat = input<boolean>(true);
  readonly hasXlsFormat = input<boolean>(true);

  // Convert Route Query Params to a Signal
  private readonly _queryParams = toSignal(this._route.queryParams, {
    initialValue: {},
  });

  // Declarative query parameter filter resolution
  readonly filterValues = computed(() => {
    const params = this._queryParams();
    // params.query is the legacy filter param; newer pages use appliedFilters
    return params['appliedFilters'] || params['query'] || '';
  });

  // Track the source table state via reactive streams converted to Signals
  private _tableService$ = toObservable(this.tableService);

  readonly isTableLoading = toSignal(
    this._tableService$.pipe(
      switchMap((service: Ag1TableService) => service.isLoading$),
      distinctUntilChanged(),
    ),
    { initialValue: false },
  );

  readonly tableData = toSignal(
    this._tableService$.pipe(
      switchMap((service: Ag1TableService) => service.data$),
      distinctUntilChanged(),
    ),
    { initialValue: [] as any[] }, // Added cast to prevent strict never[] inference issues
  );

  readonly fields = toSignal(
    this._tableService$.pipe(
      switchMap((service: Ag1TableService) => service.fields$),
      distinctUntilChanged(),
    ),
    { initialValue: [] as FieldType[] },
  );

  readonly isEmpty = computed(() => this.tableData().length === 0);

  private readonly _downloadTrigger = signal<{
    format: ItemType;
    requestId: string;
    timestamp: number;
  } | null>(null);

  downloadResource = rxResource({
    params: () => this._downloadTrigger(),
    stream: ({ params }) => {
      if (!params) {
        return EMPTY;
      }

      const { format, requestId } = params;

      // subscribe to the file generation then fetch the file if it triggered
      const generationSubscription$: Observable<Ag1TableOutputFileResult | null> =
        this._subscribeToFileGeneration$(requestId).pipe(
          switchMap(() => this._fetchFileResult$(requestId)),
        );

      // generate the file then poll for the result
      const fileGeneration$: Observable<Ag1TableOutputFileResult | null> =
        concat(
          this._fileGeneration$(format, requestId).pipe(switchMap(() => EMPTY)),
          this._pollForResult$(requestId),
        );

      // merge the generation subscription and the file generation then take the first successful result
      return merge(generationSubscription$, fileGeneration$).pipe(
        // only take the first result that has a fileUrl
        filter(
          (fileResult: Ag1TableOutputFileResult | null) =>
            fileResult && fileResult.fileUrl !== null,
        ),
        take(1),
        tap((fileResult: Ag1TableOutputFileResult) =>
          this._onFileResult(fileResult),
        ),
        timeout(120_000), // if silence for 2 minutes, throw an error
        catchError((err: unknown) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Download failed',
            detail: 'Unable to download file.',
          });
          console.error(err);
          captureException(err);
          return EMPTY;
        }),
      );
    },
  });

  // Derived computed template states
  readonly isGenerating = computed(() => this.downloadResource.isLoading());

  readonly isDisabled = computed(
    () =>
      this.isTableLoading() ||
      this.isGenerating() ||
      this.isEmpty() ||
      this.fields().length === 0,
  );

  readonly fileFormats = computed<ItemType[]>(() => {
    const formats: ItemType[] = [];
    if (this.hasPdfFormat()) {
      formats.push({ label: 'Pdf', value: TemplateFileFormatEnum.Pdf });
    }
    if (this.hasXlsFormat()) {
      formats.push({ label: 'Excel', value: TemplateFileFormatEnum.Xls });
    }
    return formats;
  });

  onDownload(selectedFormat: ItemType): void {
    if (this.isDisabled() || this.tableData().length === 0) {
      return;
    }

    this._messageService.add({
      severity: 'info',
      summary: 'Preparing download',
      detail: 'Your file is being prepared…',
    });

    this._downloadTrigger.set({
      format: selectedFormat,
      requestId: this._uuidService.generate(),
      timestamp: Date.now(),
    });
  }

  private _onFileResult(fileResult: Ag1TableOutputFileResult): void {
    window.open(fileResult.fileUrl, '_blank');
    this._messageService.add({
      severity: 'success',
      summary: 'File downloaded',
    });
  }

  private _fileGeneration$(
    format: ItemType,
    requestId: string,
  ): Observable<string> {
    return this._generatedAg1TableOutputFileGQL
      .mutate({ variables: this._fileGenerationVariables(format, requestId) })
      .pipe(
        map((res) => {
          if (!res.data?.generateAg1TableOutputFile) {
            throw new Error('Unable to generate file.');
          }
          return res.data.generateAg1TableOutputFile.requestId;
        }),
        catchError((err: unknown) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Generation failed',
            detail: 'Unable to generate file.',
          });
          console.error(err);
          captureException(err);
          throw new Error('Unable to generate file.'); // rethrow the error to kill the pipeline. Can't fetch a file if it's not generated.
        }),
      );
  }

  private _pollForResult$(
    requestId: string,
  ): Observable<Ag1TableOutputFileResult> {
    return interval(5000).pipe(
      // try fetch file result every 5 seconds
      exhaustMap(() => this._fetchFileResult$(requestId)),
    );
  }

  private _fetchFileResult$(
    requestId: string,
  ): Observable<Ag1TableOutputFileResult | null> {
    return this._ag1TableOutputFileResultGQL
      .fetch({ variables: { requestId }, fetchPolicy: 'no-cache' })
      .pipe(
        map((res) => {
          const outputFileResult: Ag1TableOutputFileResult =
            res.data?.ag1TableOutputFileResult;

          if (outputFileResult?.error) {
            throw new Error(outputFileResult.error);
          }

          return outputFileResult ?? null;
        }),
      );
  }

  private _subscribeToFileGeneration$(requestId: string): Observable<string> {
    return this._onAg1TableOutputFileCompleteGQL
      .subscribe({ variables: { requestId } })
      .pipe(
        map((res) => res.data?.onAg1TableOutputFileComplete?.requestId),
        filter((requestId: string | undefined) => requestId === requestId),
        take(1),
        catchError((err: unknown) => {
          captureException(err);
          console.error(err);
          return EMPTY;
        }),
      );
  }

  private _fileGenerationVariables(
    selectedFormat: ItemType,
    requestId: string,
  ): GenerateAg1TableOutputFileMutationVariables {
    return {
      data: this.tableData(),
      fields: this._mapDisplayNames(this.fields()),
      fileformat: selectedFormat.value as TemplateFileFormatEnum,
      tablename: this.tableName(),
      filtervalues: this.filterValues(),
      requestId,
    };
  }

  private _mapDisplayNames(fields: FieldType[]): Ag1TableFieldAttribute[] {
    return fields.map((field: FieldType) => {
      const displayName: string =
        field.displayName ||
        this._sentenceCasePipe.transform(
          this._humanizePipe.transform(field.name),
        );

      // only include fields supported by the download
      return {
        name: field.name,
        checked: field.checked,
        displayName,
        order: field.order,
        grouped: field.grouped,
        groupable: field.groupable,
        groupOrder: field.groupOrder,
        sortable: field.sortable,
        sortDirection: field.sortDirection,
        width: field.width,
      };
    });
  }
}
